import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Icon, Text, BlockStack } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';

export interface SearchableOption {
  label: string;
  value: string;
  /** Optional second line — email, SKU, status. Helps disambiguate similar names. */
  detail?: string;
}

interface SearchableSelectProps {
  label: string;
  /** Currently selected value, or '' for none. */
  value: string;
  onChange: (value: string, option?: SearchableOption) => void;
  /**
   * Server-side lookup. Called with the trimmed query — debounced, so it does not
   * fire on every keystroke. Must return at most `limit` results.
   */
  onSearch: (query: string) => Promise<SearchableOption[]>;
  /** Label for the currently selected value, so it survives a remount. */
  selectedLabel?: string;
  /** Shown as the first entry and selectable, e.g. "No clinic (Direct Order)". */
  emptyOption?: SearchableOption;
  placeholder?: string;
  helpText?: string;
  error?: string;
  disabled?: boolean;
  /** Characters required before searching. 0 loads an initial page on focus. */
  minChars?: number;
  /** Upper bound on rendered results; drives the "refine your search" hint. */
  limit?: number;
}

const DEBOUNCE_MS = 250;

/**
 * Type-ahead picker for lists too large for a native `<Select>`.
 *
 * A native select cannot be searched or filtered, so with 842 clinics or 45,480
 * patients it is unusable — the browser renders a window of the list and there is no
 * way to reach the rest. This searches on the server instead, so the size of the table
 * stops mattering.
 *
 * Results are capped, and when the cap is hit the user is told so explicitly rather
 * than being left to assume the missing record does not exist — that ambiguity is what
 * made the previous pickers feel broken.
 */
export default function SearchableSelect({
  label,
  value,
  onChange,
  onSearch,
  selectedLabel,
  emptyOption,
  placeholder,
  helpText,
  error,
  disabled,
  minChars = 0,
  limit = 25,
}: SearchableSelectProps) {
  const [input, setInput] = useState(selectedLabel ?? '');
  const [options, setOptions] = useState<SearchableOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  // Guards against a slow early request landing after a faster later one and
  // overwriting fresher results.
  const requestSeq = useRef(0);

  // Keep the field in step when the parent changes the selection (copy-from-order,
  // form reset), without clobbering what the user is actively typing.
  useEffect(() => {
    if (selectedLabel !== undefined && value) setInput(selectedLabel);
    if (!value && selectedLabel === undefined) setInput('');
  }, [selectedLabel, value]);

  const runSearch = useCallback(
    async (query: string) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      try {
        const results = await onSearch(query);
        if (seq !== requestSeq.current) return;
        setTruncated(results.length >= limit);
        setOptions(results.slice(0, limit));
      } catch {
        if (seq !== requestSeq.current) return;
        setOptions([]);
        setTruncated(false);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [onSearch, limit],
  );

  const handleInput = useCallback(
    (next: string) => {
      setInput(next);
      // Typing after a selection means the user is picking something else.
      if (value && next !== selectedLabel) onChange('', undefined);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      const trimmed = next.trim();
      if (trimmed.length < minChars) {
        setOptions([]);
        setTruncated(false);
        setLoading(false);
        return;
      }
      debounceRef.current = setTimeout(() => runSearch(trimmed), DEBOUNCE_MS);
    },
    [minChars, runSearch, value, selectedLabel, onChange],
  );

  // Load a first page on focus so the control is usable without typing.
  const handleFocus = useCallback(() => {
    if (options.length === 0 && input.trim().length >= minChars) runSearch(input.trim());
  }, [options.length, input, minChars, runSearch]);

  // Block body, not an expression: an arrow returning the result of `&&` yields
  // `void | 0 | undefined`, which is not a valid effect cleanup.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const shown = useMemo(() => {
    const list = options.map((o) => ({
      value: o.value,
      label: o.detail ? `${o.label} — ${o.detail}` : o.label,
    }));
    return emptyOption
      ? [{ value: emptyOption.value, label: emptyOption.label }, ...list]
      : list;
  }, [options, emptyOption]);

  const handleSelect = useCallback(
    (selected: string[]) => {
      const picked = selected[0];
      if (emptyOption && picked === emptyOption.value) {
        onChange(emptyOption.value, emptyOption);
        setInput(emptyOption.label);
        return;
      }
      const option = options.find((o) => o.value === picked);
      if (!option) return;
      onChange(option.value, option);
      setInput(option.label);
    },
    [options, emptyOption, onChange],
  );

  return (
    <BlockStack gap="100">
      <Autocomplete
        options={shown}
        selected={value ? [value] : []}
        onSelect={handleSelect}
        loading={loading}
        textField={
          <Autocomplete.TextField
            label={label}
            value={input}
            onChange={handleInput}
            onFocus={handleFocus}
            prefix={<Icon source={SearchIcon} tone="base" />}
            placeholder={placeholder ?? 'Type to search…'}
            helpText={helpText}
            error={error}
            disabled={disabled}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => {
              setInput('');
              setOptions([]);
              setTruncated(false);
              onChange('', undefined);
            }}
          />
        }
      />
      {truncated && (
        <Text as="p" variant="bodySm" tone="subdued">
          Showing the first {limit} matches — keep typing to narrow it down.
        </Text>
      )}
    </BlockStack>
  );
}
